import { useEffect } from 'react';
import { useBoolean } from 'ahooks';

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import { cn } from '@rocco/utils/cn';
import type { FamilyBrandOption } from '@modules/professional/hook/use-family-brand-filter';

/** Scroll distance that dismisses the open popover (matches the Display dropdown). */
const SCROLL_CLOSE_DELTA_PX = 300;

/**
 * The Products tab's brand-family filter, as a grey pill opening a checkbox list.
 *
 * A row of logo marks put every brand on screen at once, but a brand mark at that
 * size is not recognisable — `CompanyLogo` is a square canvas with the lockup
 * padded inside it — and the row grew horizontally with each brand added. The pill
 * names the profile's own brand (plus a count of the siblings switched on) and
 * keeps the rest in the list, so the bar's width no longer depends on how large
 * the family is.
 *
 * Rows are links, not checkbox inputs: the selection lives in the URL so it stays
 * shareable, and the checkbox is the visual for the state the loader applied.
 * Toggling navigates within the same route, so the popover survives it and a
 * visitor can tick several brands in one pass — each tick shows immediately,
 * from the pending URL, while the search resolves behind it.
 */
export const ProfessionalBrandFamilyDropdown = ({
    brands,
}: {
    brands: FamilyBrandOption[];
}) => {
    const [isOpen, { set: setIsOpen, setFalse: close }] = useBoolean(false);

    // Attached only while open so the baseline is the scroll position at open
    // time; a listener attached at mount would carry a stale baseline and
    // dismiss the popover on a tiny scroll.
    useEffect(() => {
        if (!isOpen) return;
        const openScrollY = window.scrollY;
        const handleScroll = () => {
            if (
                Math.abs(window.scrollY - openScrollY) > SCROLL_CLOSE_DELTA_PX
            ) {
                close();
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isOpen, close]);

    // The hook lists this profile first, so the label names the brand whose page
    // this is and the count carries the siblings — the trigger stays one width
    // whether the family has two brands or ten.
    const topBrand = brands[0];
    if (!topBrand) return null;
    const extraSelected = brands.filter(
        (brand) => brand.checked && brand.segment !== topBrand.segment,
    ).length;

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="brand-family-trigger"
                    className={cn(
                        'group flex h-10.5 max-w-[24rem] shrink-0 items-center gap-2 rounded-full bg-[#f2f2f2] pr-4 pl-2 text-base whitespace-nowrap text-[#323232] transition-colors hover:bg-[#e8e8e8]',
                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                    )}
                >
                    <ProfessionalTileLogo
                        image={{ src: topBrand.logo, alt: '' }}
                        background={topBrand.logoBackground}
                        className="size-8 shrink-0"
                    />
                    {/* Prefix rather than an `aria-label`, which would replace
                        the brand name and count with the bare purpose. */}
                    <span className="sr-only">Filter by brand:</span>
                    <span className="truncate">{topBrand.title}</span>
                    {extraSelected > 0 ? (
                        <span className="shrink-0 text-[#323232]/60">
                            +{extraSelected}
                        </span>
                    ) : null}
                    <ArrowChevronDownIcon className="size-4.5 shrink-0 transition-transform group-data-[state=open]:transform-[rotateX(180deg)]" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" paddings="small" className="w-80">
                <p className="m-0 px-2 pt-1 pb-2 font-sans text-[0.875rem] text-[#343434]/60">
                    Show products from
                </p>
                {brands.map((brand) => (
                    <RoccoLink
                        key={brand.segment}
                        to={brand.to}
                        preventScrollReset
                        // Deliberately NOT `prefetch="intent"`. It does fire —
                        // hovering a row requests the route's `.data` — but the
                        // response carries no cache headers, so the click issues
                        // an identical second request and the prefetch is thrown
                        // away. Measured: a 177KB prefetch on hover, then a 177KB
                        // fetch on click, with the click no faster. That is one
                        // wasted product search per row the pointer crosses.
                        aria-pressed={brand.checked}
                        className="flex cursor-pointer items-center gap-x-3 rounded-2 px-2 py-2.5 hover:bg-gray-50"
                    >
                        {/* Drawn rather than a Radix `Checkbox`: that renders a
                            real button, and a button inside this row's anchor is
                            invalid HTML that assistive tech may still surface
                            however it is hidden. The row is a link carrying
                            `aria-pressed`, so the box is pure decoration. */}
                        <span
                            aria-hidden="true"
                            className={cn(
                                'flex size-[0.9375rem] shrink-0 items-center justify-center rounded-[0.125rem] border',
                                brand.checked
                                    ? 'border-[#343434] bg-[#343434]'
                                    : 'border-[#343434]',
                            )}
                        >
                            {brand.checked ? (
                                <svg
                                    viewBox="0 0 12 12"
                                    className="size-[0.65rem] text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.9"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M2 6.3l2.6 2.6L10 3.4" />
                                </svg>
                            ) : null}
                        </span>
                        <ProfessionalTileLogo
                            image={{ src: brand.logo, alt: '' }}
                            background={brand.logoBackground}
                            className="size-7 shrink-0"
                        />
                        <span className="text-[0.875rem] leading-[1.358] text-[#343434]">
                            {brand.title}
                        </span>
                    </RoccoLink>
                ))}
            </PopoverContent>
        </Popover>
    );
};
